import { gsap } from 'gsap';
import {
    type BufferGeometry,
    Color,
    Mesh,
    Object3D,
    type PlaneGeometry,
    ShaderMaterial,
    Vector2,
    Vector3,
    WebGLRenderTarget,
} from 'three';

import type LayerManager from './LayerManager';

import fragmentShader from '../shaders/copy.frag.glsl';
import vertexShader from '../shaders/copy.vert.glsl';
import { FloorGameWebGLVariant } from '../types';
import { type LayerSceneType } from '../types/layerTypes';
import LayerObserver from '../utils/LayerObserver';
import PerformanceProfile from '../utils/PerformanceProfile';

interface DOMOptions {
    isScrollingElement: boolean;
    isStickyTop: boolean;
    isStickyBottom: boolean;
}

export default class Layer extends Object3D {
    layerId: string | number;
    geom: PlaneGeometry | BufferGeometry;
    pixelRatio: number;
    width: number;
    height: number;
    x: number;
    y: number;
    manager: LayerManager;
    zIndex: number;
    scene: LayerSceneType;
    useObserver: boolean;
    observers: unknown;
    needUpdate: boolean;
    borderRadius: number;
    isScrollingElement: boolean;
    isStickyTop: boolean;
    isStickyBottom: boolean;
    fadeBottom: boolean;
    canRender: boolean;
    isEnabled: boolean;
    needLateUpdate: boolean;
    skipPerformanceCheck: boolean;
    rt!: WebGLRenderTarget;
    layerObserver!: LayerObserver;
    domElement: HTMLElement | unknown;
    backgroundColor?: string | null;
    backgroundAlpha?: number;
    material: ShaderMaterial | unknown;
    mesh!: Mesh<BufferGeometry, ShaderMaterial>;
    layerType: FloorGameWebGLVariant | null;

    constructor(options) {
        super();
        this.layerId = options.id || 'unknown-id';
        this.geom = options.geometry ? options.geometry : options.manager.planeGeometry;
        this.pixelRatio = options.pixelRatio ? options.pixelRatio : 2;
        this.width = options.width ? options.width : 1;
        this.height = options.height ? options.height : 1;
        this.x = options.x ? options.x : 0;
        this.y = options.y ? options.y : 0;
        this.manager = options.manager;
        this.zIndex = options.zIndex ? options.zIndex : 1;
        this.scene = options.scene;
        this.useObserver = options.useObserver;
        if (this.useObserver) this.observers = options.observers;
        this.needUpdate = options.needUpdate || false;
        this.needLateUpdate = options.needLateUpdate || false;
        this.borderRadius = options.borderRadius || 0;
        this.isScrollingElement = options.isScrollingElement || false;
        this.isStickyTop = options.isStickyTop || false;
        this.isStickyBottom = options.isStickyBottom || false;
        this.fadeBottom = options.fadeBottom || false;
        this.skipPerformanceCheck = options.skipPerformanceCheck || false;
        this.backgroundColor = options.backgroundColor || null;
        this.backgroundAlpha = options.backgroundAlpha || 1.0;

        this.layerType = options.type || null;
        this.canRender = true;

        this.scene.layer = this;
        this.isEnabled = true;

        this.initBase();
        this.initMesh();
        if (this.useObserver) {
            this.initObservers();
        } else {
            this.setSize(this.width, this.height);
            this.setPosition({ left: this.x, top: this.y }, this.zIndex);
        }
    }

    initBase() {
        this.rt = new WebGLRenderTarget(this.width * this.pixelRatio, this.height * this.pixelRatio, {
            samples: PerformanceProfile.globalProfile.rtSamples || 4.0,
        });
    }

    updatePerformanceProfile() {
        if (this.skipPerformanceCheck) return;

        this.pixelRatio = PerformanceProfile.globalProfile.maxDpr;
        if (this.visible) {
            // eslint-disable-next-line no-console
            if (PerformanceProfile.debug) console.log('Update Layer DPR', this.layerId, this.pixelRatio);
            this.setSize(this.width, this.height, true);
        }
    }

    initObservers() {
        this.layerObserver = new LayerObserver({
            observers: this.observers,
            isScrollingElement: this.isScrollingElement,
            isStickyTop: this.isStickyTop,
            isStickyBottom: this.isStickyBottom,
            domElement: this.domElement || false,
            id: this.layerId,
            manager: this.manager,
        });
    }

    initMesh() {
        const material = new ShaderMaterial({
            uniforms: {
                uTexture: { value: this.rt.texture },
                uBorderRadius: { value: this.borderRadius * this.pixelRatio },
                uResolution: { value: new Vector2(this.width, this.height) },
                uAlpha: { value: 1.0 },
                uTransition: { value: 0.0 },
                uBackgroundColor: {
                    value: this.backgroundColor ? new Color(this.backgroundColor) : new Vector3(0.0, 0.0, 0.0),
                },
                uBackgroundAlpha: { value: this.backgroundAlpha || 1.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            defines: {
                BORDER_RADIUS: this.borderRadius > 0.0,
                FLOOR_GAME_UP: this.layerType === FloorGameWebGLVariant.TierUp,
                FADE_BOTTOM: this.fadeBottom,
                USE_BACKGROUND_COLOR: !!this.backgroundColor,
            },
        });

        this.material = material;

        this.mesh = new Mesh(this.geom, material);

        this.add(this.mesh);
    }

    updateDomElement(domElement, options = {} as DOMOptions) {
        if (Object.prototype.hasOwnProperty.call(options, 'isScrollingElement')) {
            if (options.isScrollingElement !== this.isScrollingElement) {
                this.isScrollingElement = options.isScrollingElement;
                this.layerObserver.isScrollingElement = options.isScrollingElement;
            }
        }

        if (Object.prototype.hasOwnProperty.call(options, 'isStickyTop')) {
            if (options.isStickyTop !== this.isStickyTop) {
                this.isStickyTop = options.isStickyTop;
                this.layerObserver.isStickyTop = options.isStickyTop;
            }
        }

        if (Object.prototype.hasOwnProperty.call(options, 'isStickyBottom')) {
            if (options.isStickyBottom !== this.isStickyBottom) {
                this.isStickyBottom = options.isStickyBottom;
                this.layerObserver.isStickyBottom = options.isStickyBottom;
            }
        }

        this.layerObserver.observeElement(domElement, this.updateFromObserver.bind(this));

        const box = this.layerObserver.box;
        this.setSize(box.width, box.height);
        this.setPosition(box);

        // In case interaction is needed
        this.scene.setDomElement && this.scene.setDomElement(domElement);
    }

    unobserveElement() {
        this.layerObserver.unobserveElement();
    }

    updateFromObserver(box) {
        this.setSize(box.width, box.height);
        this.setPosition(box);
    }

    setSize(width, height, force = false) {
        if (this.width === Math.round(width) && this.height === Math.round(height) && !force) return;

        width = Math.max(width, 1);
        height = Math.max(height, 1);

        this.width = Math.round(width);
        this.height = Math.round(height);

        this.mesh.scale.set(this.width, this.height, 1);
        // Check Perf Profile
        this.rt.setSize(this.width * this.pixelRatio, this.height * this.pixelRatio);

        this.mesh.position.x = this.width * 0.5;
        this.mesh.position.y = -this.height * 0.5;

        this.mesh.material.uniforms.uResolution.value.x = this.width * this.pixelRatio;
        this.mesh.material.uniforms.uResolution.value.y = this.height * this.pixelRatio;

        this.mesh.material.uniforms.uBorderRadius.value = this.borderRadius * this.pixelRatio;

        this.scene.setSize && this.scene.setSize(width * this.pixelRatio, height * this.pixelRatio);
    }

    setPosition(box, zIndex = this.zIndex) {
        this.x = box.left;
        if (this.useObserver && this.isScrollingElement) this.y = box.scrollingTop;
        else this.y = box.top;
        this.zIndex = zIndex;

        this.position.set(this.x, -this.y, zIndex);
    }

    setBackgroundColor(color, alpha = 1.0) {
        this.backgroundColor = color;
        this.backgroundAlpha = alpha;
        this.mesh.material.uniforms.uBackgroundColor.value = new Color(color);
        this.mesh.material.uniforms.uBackgroundAlpha.value = alpha;

        this.mesh.material.defines.USE_BACKGROUND_COLOR = true;

        this.mesh.material.needsUpdate = true;
    }

    unsetBackgroundColor() {
        this.backgroundColor = null;
        this.backgroundAlpha = 1.0;
        this.mesh.material.defines.USE_BACKGROUND_COLOR = false;
        this.mesh.material.needsUpdate = true;
    }

    render(elapsed, delta, needScrollUpdate) {
        if (!this.scene) return;

        if (!this.isEnabled) return;

        if (this.useObserver) this.visible = this.layerObserver.isInView;

        if (!this.visible) return;

        if (this.needUpdate) {
            this.scene.update && this.scene.update(elapsed, delta);
        }

        if (!this.manager.capFPS) {
            const canScrollRender = this.manager.disableRenderingOnScroll ? !needScrollUpdate : true;
            if (this.canRender && canScrollRender) {
                this.renderFrame();
            }
        }

        this.layerObserver && this.layerObserver.update(needScrollUpdate);

        if (this.isScrollingElement && this.isStickyTop) {
            this.position.y = -Math.max(this.layerObserver.box.stickyTop - this.manager.scrollY, 0);
        } else if (this.isScrollingElement && this.isStickyBottom) {
            // if (this.manager.scrollHeight > 0.0) {
            //     console.log(
            //         `TopPosition:${this.layerObserver.box.stickyTop}`,
            //         `ScrollY:${this.manager.scrollY}`,
            //         `WindowHeight:${this.manager.height}`,
            //         `ScrollerHeight: ${this.manager.scrollHeight}`
            //     );

            //     const offsetBottom = Math.max(
            //         this.manager.scrollY + this.manager.height - this.manager.scrollHeight,
            //         0.0
            //     );
            //     this.position.y = -this.layerObserver.box.stickyTop + offsetBottom;
            // }
            if (needScrollUpdate) {
                // TODO THUNDER: Find a more performant solution
                this.layerObserver.resizeNotFrameBased();
                this.position.y = -this.layerObserver.box.top;
            }
        } else if (this.isScrollingElement) {
            this.position.y = -this.layerObserver.box.top;
        }

        // eslint-disable-next-line no-console
        if (this.manager.debugLayer) console.log('Rendered Layer', this.layerId);
    }

    lateRender() {
        if (!this.needLateUpdate) return;
        if (!this.visible) return;

        this.scene.lateUpdate && this.scene.lateUpdate();
    }

    renderFrame() {
        this.manager.renderer.setRenderTarget(this.rt);
        this.manager.renderer.render(this.scene.scene, this.scene.camera);
        this.manager.renderer.setRenderTarget(null);
    }

    enable() {
        this.isEnabled = true;
        if (!this.useObserver) this.visible = true;
    }

    disable() {
        this.isEnabled = false;
        this.visible = false;
    }

    delete() {
        this.disable();
        this.layerObserver && this.layerObserver.delete();
        this.rt.dispose();
        this.mesh.material.dispose();
        this.mesh.geometry.dispose();

        this.scene.delete && this.scene.delete();
    }

    animateIn(type = 'fade-in') {
        if (type === 'fade-in') {
            gsap.fromTo(
                this.mesh.material.uniforms.uAlpha,
                {
                    value: 0,
                },
                {
                    value: 1.0,
                    duration: 0.6,
                    ease: 'power2.out',
                },
            );
        } else {
            this.mesh.material.uniforms.uAlpha.value = 1.0;
        }
    }

    async animateOut(type = 'fade-out') {
        if (type === 'fade-out') {
            await gsap.to(this.mesh.material.uniforms.uAlpha, {
                value: 0,
                duration: 1.0,
                ease: 'power2.out',
            });
        }
    }
}
